import { Column, Row } from 'app/design/primitives/box';
import { Input } from 'app/design/primitives/input';
import { ControlledInput } from 'app/design/primitives/input/controlled';
import { Modal } from 'app/design/primitives/modal';
import { Text } from 'app/design/primitives/text';

import type { CreateProfileDTO } from 'app/types/profile';
import type { User } from 'app/types/user';
import { FeatureFlag, isFeatureEnabled } from 'app/utils/helpers';
import type { TFunction } from 'i18next';
import { Controller, useController, type Control } from 'react-hook-form';
import type { TextInput } from 'react-native';
import { Pressable, View } from 'react-native';
import { ImagePicker } from 'app/design/primitives/image-picker';
import { createTheme as createDSTheme } from 'app/shopify';
import { IconInfoCircle, IconPlus, IconX } from '@tabler/icons-react-native';
import { ICON_STROKE_WIDTH } from 'app/utils/constants';
import { useGroupsByType } from 'app/network/groups';
import { GroupType, type GroupByTypeSummary } from 'app/types/group-base';
const theme = createDSTheme();
const ACTIVITY_SECTOR_FEATURE = FeatureFlag.useActivitySector;

export interface PersonalInformationFormFieldsProps {
  control: Control<CreateProfileDTO>;
  onOpenPhoneInfo: () => void;
  refAdditionalName: React.RefObject<TextInput | null>;
  refLastName: React.RefObject<TextInput | null>;
  refPhoneNumber: React.RefObject<TextInput | null>;
  t: TFunction;
  tSuffix: string;
  userInfo: User | null | undefined;
}

export function PersonalInformationFormFields({
  control,
  onOpenPhoneInfo,
  refAdditionalName,
  refLastName,
  refPhoneNumber,
  t,
  tSuffix,
  userInfo,
}: PersonalInformationFormFieldsProps) {
  return (
    <Column gap="spacing.general.x5">
      <Controller
        {...{ control }}
        name="picture"
        render={({ field, fieldState }) => (
          <ImagePicker
            error={fieldState.error}
            onChange={field.onChange}
            subtitle={t(`pictureFieldSubtitle${tSuffix}`)}
            title={t(`pictureFieldTitle${tSuffix}`)}
            value={field.value}
          />
        )}
      />

      <Controller
        {...{ control }}
        name="firstName"
        render={({ field, fieldState }) => (
          <Input
            autoCapitalize="words"
            autoComplete="name-given"
            error={fieldState.error}
            label={t(`firstNameFieldLabel${tSuffix}`)}
            onBlur={field.onBlur}
            onChangeText={field.onChange}
            onSubmitEditing={() =>
              isFeatureEnabled(FeatureFlag.additionalName, userInfo?.featureFlags)
                ? refAdditionalName.current?.focus()
                : isFeatureEnabled(FeatureFlag.lastName, userInfo?.featureFlags)
                  ? refLastName.current?.focus()
                  : refPhoneNumber.current?.focus()
            }
            placeholder={t(`firstNameFieldPlaceholder${tSuffix}`)}
            returnKeyType="next"
            submitBehavior="submit"
            testID="personal-firstname-input"
            value={field.value}
          />
        )}
      />

      {isFeatureEnabled(FeatureFlag.additionalName, userInfo?.featureFlags) ? (
        <Controller
          {...{ control }}
          name="additionalName"
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="words"
              error={fieldState.error}
              innerRef={refAdditionalName}
              label={t('additionalNameFieldLabel')}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() =>
                isFeatureEnabled(FeatureFlag.lastName, userInfo?.featureFlags)
                  ? refLastName.current?.focus()
                  : refPhoneNumber.current?.focus()
              }
              placeholder={t('additionalNameFieldPlaceholder')}
              returnKeyType="next"
              submitBehavior="submit"
              value={field.value ?? ''}
            />
          )}
        />
      ) : null}

      {isFeatureEnabled(FeatureFlag.lastName, userInfo?.featureFlags) ? (
        <Controller
          {...{ control }}
          name="lastName"
          render={({ field, fieldState }) => (
            <Input
              autoCapitalize="words"
              autoComplete="name-family"
              error={fieldState.error}
              innerRef={refLastName}
              label={t(`lastNameFieldLabel${tSuffix}`)}
              onBlur={field.onBlur}
              onChangeText={field.onChange}
              onSubmitEditing={() => refPhoneNumber.current?.focus()}
              placeholder={t(`lastNameFieldPlaceholder${tSuffix}`)}
              returnKeyType="next"
              submitBehavior="submit"
              testID="personal-lastname-input"
              value={field.value}
            />
          )}
        />
      ) : null}

      {isFeatureEnabled(ACTIVITY_SECTOR_FEATURE) ? <ActivitySectorsChips {...{ control, t }} /> : null}

      <View>
        <ControlledInput
          {...{ control }}
          autoComplete="tel"
          innerRef={refPhoneNumber}
          label={t('phoneFieldLabel')}
          name="phone"
          testID="personal-phone-input"
          textContentType="telephoneNumber"
          labelRight={
            <Pressable onPress={onOpenPhoneInfo} style={{ position: 'relative', bottom: 5, marginBottom: -10 }}>
              <IconInfoCircle strokeWidth={ICON_STROKE_WIDTH} color={theme.colors['icon.secondary']} />
            </Pressable>
          }
        />
        <Text variant="bodyBase" style={{ marginTop: 4, color: theme.colors['text.placeholder'] }}>
          {t('phoneFieldSubtitle')}
        </Text>
      </View>
    </Column>
  );
}

export function PhoneFieldHelpModal({ isOpen, onClose, t }: { isOpen: boolean; onClose: () => void; t: TFunction }) {
  return (
    <Modal onClose={onClose} open={isOpen} transparent>
      <Modal.Card>
        <Modal.Title allowClose onClose={onClose}>
          {t('phoneFieldHelp.title')}
        </Modal.Title>

        <Column
          gap="spacing.general.x4"
          style={{
            paddingHorizontal: theme.spacing['spacing.general.x4'],
            paddingBottom: theme.spacing['spacing.general.x8'],
          }}
        >
          <Text fontWeight={700} variant="headerXSmall">
            {t('phoneFieldHelp.boldText')}
          </Text>
          <Text variant="bodyBase" fontSize={16} lineHeight={22}>
            {t('phoneFieldHelp.text')}
          </Text>
        </Column>
      </Modal.Card>
    </Modal>
  );
}

function ActivitySectorsChips({ control, t }: { control: Control<CreateProfileDTO>; t: TFunction }) {
  const { data: groups, isLoading } = useGroupsByType(GroupType.sector);
  const { field } = useController({ control, name: 'groupIds' });
  const selected: string[] = field.value ?? [];

  const toggle = (id: GroupByTypeSummary['id']) => {
    const next = selected.includes(id) ? selected.filter((s) => s !== id) : [...selected, id];
    field.onChange(next);
  };

  if (isLoading || !groups || groups.length === 0) return null;

  const selectedIds = new Set(selected);
  return (
    <Column gap="spacing.general.x2">
      <Text fontWeight={700} variant="bodyLarge">
        {t('activitySectorsFieldLabel')}
      </Text>
      <Text variant="bodyBase" style={{ color: theme.colors['text.placeholder'] }}>
        {t('activitySectorsFieldSubtitle')}
      </Text>
      <Row style={{ flexWrap: 'wrap', gap: theme.spacing['spacing.general.x2'] }}>
        {groups.map((group) => {
          const isSelected = selectedIds.has(group.id);
          return (
            <Pressable
              key={group.id}
              accessibilityRole="checkbox"
              accessibilityState={{ checked: isSelected }}
              onPress={() => toggle(group.id)}
              style={{
                paddingVertical: theme.spacing['spacing.general.x1'],
                paddingHorizontal: theme.spacing['spacing.general.x2'],
                borderRadius: theme.borderRadii['radius.xl'],
                backgroundColor: isSelected ? theme.colors['text.primary'] : 'white',
                borderWidth: 1,
                borderColor: isSelected
                  ? theme.colors['text.primary']
                  : theme.colors['surface.inverse.tertiary-neutral'],
              }}
            >
              <Row alignItems="center" gap="spacing.general.x1">
                {!isSelected ? (
                  <IconPlus strokeWidth={ICON_STROKE_WIDTH} size={20} color={theme.colors['text.primary']} />
                ) : null}
                <Text style={{ fontWeight: '700', color: isSelected ? 'white' : theme.colors['text.primary'] }}>
                  {group.name}
                </Text>
                {isSelected ? <IconX strokeWidth={ICON_STROKE_WIDTH} size={20} color="white" /> : null}
              </Row>
            </Pressable>
          );
        })}
      </Row>
    </Column>
  );
}
